import * as React from 'react';
import ReportContainer from '@containers/reports/ReportContainer';
import { IReportColumn, IReportDefinition, IReportOutput, IReportOutputAction } from '@models/reports';
import { ISecurity } from '@models';
import { search, searchExportToXls, paginate } from '@store/actions/search';
import {
    useDataTable, useDialogs, useLoading, useTranslation, useSearchFilters, Portal,
    classNames, Paginator
} from '@components';
import { downloadBlob } from '@utils/file-utils';
import { interpolate } from '@utils';
import './ReportOutputTableComponent.scss';
import dateUtils from '@utils/date-utils';
import { useDispatch } from 'react-redux';
import useSynchronizedDispatch from '@components/custom/useSynchronizedDispatch';

export interface IProps {
    filtersContext: any;
    filterParameters?: any;
    report: IReportDefinition;
    output: IReportOutput;
    security: ISecurity;
    workId: number;
}

const parseColumn = (c: IReportColumn) => {
    if (!c.hidden) {
        return {
            title: c.title,
            className: classNames(c.className, 'center'),
            field: c.name,
            // render: (d: any) => d[c.name],
            delegate: c.delegate,
            sortKey: c.name,
        };
    }
}

const parseColumns = (c: IReportColumn[] | undefined) => {
    if (c) {
        return c.map(parseColumn);
    }
    else {
        return [];
    }
}

interface ISubReport {
    name: string;
    data: any;
    title?: string;
}

export function ReportOutputTableComponent(props: IProps) {
    const { t } = useTranslation();
    const [data, setData] = React.useState<any[]>([]);
    const [columns, setColumns] = React.useState<any[]>([]);
    const [total, setTotal] = React.useState<number>(0);

    const [first, setFirst] = React.useState(0);
    const [rows, setRows] = React.useState(100);
    const dialogs = useDialogs();

    const loading = useLoading();

    const report = props.report;
    const output = props.output;

    const initialize = loading.wrap(async (value: any, row: number|undefined = undefined, pageLimit: number|undefined = undefined) => {

        setFirst(row ?? 0);
        setRows(pageLimit ?? 100);

        setColumns(parseColumns(output.columns));
        if (output.autoStart) {
            const res = await paginate(props.workId, output.searchName, {
                filters: value.filters ?? userFilters.filters,
                page: row ?? 0,
                limit: pageLimit ?? 100,
            });

            if (res.hasValue) {

                setData(res.value.data ?? []);
                setTotal(res.value.total ?? 0);
            }
        }
    });

    const dispatcher = useSynchronizedDispatch(initialize);

    const parseAction = (c: IReportOutputAction) => {
        if (c.action === 'open') {
            return {
                icon: c.icon,
                title: c.title,
                onClick: (r: any) => {
                    const url = interpolate(c.parameter ?? '', r);
                    window.open(url, '_blank', 'location=no,status=no');
                }
            }
        }
        else if (c.action === 'report' || c.action === 'sublist') {
            return {
                icon: c.icon,
                tooltip: c.title,
                onClick: (r: any) => {
                    dialogs.show('sublist', {
                        name: c.parameter,
                        data: r,
                        title: c.title,
                    });
                }
            }
        }
        else {
            return undefined;
        }
    }

    const parseActions = (c: IReportOutputAction[] | undefined) => {
        if (c) {
            return c.map(parseAction).filter(t => t);
        }
        else {
            return [];
        }
    }


    const userFilters = useSearchFilters({
        security: props.security,
        workId: props.workId,
        name: output.searchName,
        references: props.filtersContext,
        persist: true,
        //TODO: Remove this line when the workId is not necessary
        awaitForApply: props.workId != 27 ? true : false,
    });

    React.useEffect(() => {
        dispatcher.append({
            report: props.report,
            parameters: props.filterParameters,
            filters: userFilters.filters,
        });
    }, [props.report, props.filterParameters, userFilters.filters]);

    const actions = parseActions(props.output.actions);

    const dataTable = useDataTable({
        actions: actions,
        columns: columns,
        data: data,
        scrollHeight: '100%',
        scrollable: true,
        className: `ReportOutputTableComponent ${props.report.className}`,
        tooltip: true,
        resizableColumns: true,
        lazy: true,
        paginator: false,
        onPage: (pageRow, pageLimit) => initialize(userFilters.filters, pageRow, pageLimit),
        totalRows: total,
        rows: 100,
        rowsPerPageOptions: [100, 200, 300]
    });

    const exportToXls = async (filters: any, filename: string | undefined = undefined) => {
        const resp = await searchExportToXls(props.workId, output.searchName, {
            filters: filters,
        });
        downloadBlob(resp, filename ?? (report.title + '.xlsx'));
    }

    return <div className={'c he e'}>
        {loading.renderBox()}
        <Portal container='#breadcrumb-right'>
            <div className='r g-20' style={{ alignItems: 'center' }}>
                {output.canExport && <span className='exporter'>
                    <img
                        src='img/icons/excel-export-download.png'
                        className='pointer'
                        title={t('paginator.export')}
                        onClick={() => exportToXls(userFilters.filters)}
                        style={{ height: '23px', marginTop: '3px' }} />
                </span>}
                {userFilters.renderAsButton({ className: 'fas fa-filter pointer' })}
            </div>
        </Portal>

        {dataTable()}

        <Paginator
            className='paginator e'
            template={'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink'}
            first={ first * rows }
            rows={rows}
            totalRecords={total}
            rowsPerPageOptions={[100, 200, 300]}
            onPageChange={(page : {first: number, rows: number, page: number, pageCount: number}) =>  initialize(userFilters.filters, page.page ?? 0, page.rows ?? 100) }
            />

        {dialogs.render('sublist', {}, (data: ISubReport) =>
            <div style={{ width: '90vw', height: '90vh' }}>
                <div className='r md pd subreport-header'>
                    <span className='e'>{data.title}</span>
                    <i className='fa fa-times pointer' onClick={_ => dialogs.clear()} />
                </div>
                <ReportContainer
                    filterParameters={data.data}
                    workId={props.workId}
                    reportName={data.name} />
            </div>)}
    </div>
}
